import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { addgallery, viewall, viewgallery } from "../Apis/gallery";
import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

const initialState = {
  data: [],
  totalRecords: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 10,
  loading: false,
  error: null,
};

// Allowed file types
const validImageTypes = ["image/jpeg", "image/png", "image/gif"];
const validVideoTypes = ["video/mp4", "video/webm", "video/avi"];

// Async thunk to upload media files to Firebase and then add to the gallery
export const addGallery = createAsyncThunk(
  "gallery/register",
  async ({ image, desp }, { rejectWithValue }) => {
    let mediaURLs = [];

    if (!image || image.length === 0) {
      return rejectWithValue("Please upload at least one valid media file.");
    }

    try {
      const storage = getStorage();

      // Upload all files in parallel using Promise.all()
      const uploadPromises = image.map((file) => {
        return new Promise((resolve, reject) => {
          const fileType = file.type;
          let fileCategory = "";

          if (validImageTypes.includes(fileType)) {
            fileCategory = "images";
          } else if (validVideoTypes.includes(fileType)) {
            fileCategory = "videos";
          } else {
            return reject(
              "Invalid file type. Only images and videos are allowed."
            );
          }

          const fileName = `${file.name}-${Math.random()
            .toString(36)
            .substr(2, 9)}.${file.name.split(".").pop()}`;
          const storageRef = ref(
            storage,
            `/uploads/${fileCategory}/${fileName}`
          );
          const uploadTask = uploadBytesResumable(storageRef, file);

          uploadTask.on(
            "state_changed",
            null,
            (error) => reject(error),
            async () => {
              try {
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref
                );
                resolve({ url: downloadURL, type: fileCategory });
              } catch (error) {
                reject(error);
              }
            }
          );
        });
      });

      // Wait for all uploads to complete
      mediaURLs = await Promise.all(uploadPromises);

      // Once all files are uploaded, call the API to store media information
      const response = await addgallery(mediaURLs, desp);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || "Media upload failed");
    }
  }
);

// Async thunk to fetch gallery data
export const fetchGallery = createAsyncThunk(
  "gallery/view",
  async ({ page, pageSize }, { rejectWithValue }) => {
    try {
      const response = await viewgallery(page, pageSize);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

export const fetchAllGallery = createAsyncThunk(
  "gallery/viewWithoutPagination",
  async ({ rejectWithValue }) => {
    try {
      const response = await viewall();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to fetch data");
    }
  }
);

const gallerySlice = createSlice({
  name: "gallery",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Handle media upload and gallery addition
      .addCase(addGallery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addGallery.fulfilled, (state, action) => {
        state.loading = false;
        state.data.unshift(action.payload); // Add new media to gallery list
      })
      .addCase(addGallery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Something went wrong";
      })

      // Handle fetching gallery data
      .addCase(fetchGallery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGallery.fulfilled, (state, action) => {
        state.loading = false;
        state.galleryData = action.payload.data;
        state.totalRecords = action.payload.totalRecords;
        state.totalPages = action.payload.totalPages;
        state.currentPage = action.payload.currentPage;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchGallery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Handle fetching gallery all data
      .addCase(fetchAllGallery.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllGallery.fulfilled, (state, action) => {
        state.loading = false;        
        state.galleryAllData = action.payload;
      })
      .addCase(fetchAllGallery.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default gallerySlice.reducer;
